import { get, post, patch, del } from '@/api/rest'
import {
	setChat,
	setChats,
	setMembership,
	setMembershipsSafe,
	deleteMembership,
	deleteChat,
	getMyUserId,
} from '@/store'
import { getMyMembershipForBubble } from '@/store/memberships'

/**
 * Thread API Layer
 *
 * Handles API calls for thread chats - lightweight chats that exist
 * under a parent chat. Not to be confused with reply chains (message
 * parent/child relationships within a single chat).
 */

/**
 * Create a new thread in a chat
 * @param {number} chatId - Parent chat ID
 * @param {Object} options - Thread creation options
 * @param {string} options.title - Thread title (required)
 * @param {number} [options.source_message_id] - Message that spawned the thread
 * @param {boolean} [options.convert_reply_chain] - If true, converts existing reply chain to thread
 * @returns {Promise<Object>} Created thread chat object (is_thread=true)
 */
export const createThread = async (
	chatId,
	{ title, source_message_id, convert_reply_chain }
) => {
	try {
		const payload = { title }
		if (source_message_id) {
			payload.source_message_id = source_message_id
		}
		if (convert_reply_chain) {
			payload.convert_reply_chain = true
		}
		const thread = await post(`/chats/${chatId}/threads`, payload)
		setChat(thread)
		return thread
	} catch (e) {
		console.error('createThread error:', e)
		throw e
	}
}

/**
 * Fetch threads for a chat with pagination
 * @param {number} chatId - Parent chat ID
 * @param {Object} [params] - Pagination params
 * @param {number} [params.page] - Page number
 * @param {number} [params.size] - Page size
 * @returns {Promise<Object>} Paginated response with thread chats
 */
export const fetchThreadsForChat = async (chatId, params = {}) => {
	try {
		const resp = await get(`/chats/${chatId}/threads`, {
			'page[size]': params.size || 20,
			include: 'latest_user_message',
			...params,
		})
		return resp
	} catch (e) {
		console.error('fetchThreadsForChat error:', e)
		throw e
	}
}

/**
 * Fetch threads the current user has joined for a specific parent chat.
 * Uses the memberships endpoint with thread filters and transforms
 * the response so .data contains chat/thread objects (matching the
 * shape returned by fetchThreadsForChat).
 * @param {number} chatId - Parent chat ID
 * @param {Object} [params] - Additional params (e.g. filter[search])
 * @returns {Promise<Object>} Paginated response with thread chat objects
 */
const transformMembershipResp = (resp) => {
	const memberships = []
	const threads = (resp.data || []).map((membership) => {
		const { chat, ...m } = membership
		memberships.push(m)
		return chat
	})
	if (threads.length) setChats(threads)
	setMembershipsSafe(memberships)

	const transformed = { ...resp, data: threads }
	if (resp.next) {
		const originalNext = resp.next
		transformed.next = async () => transformMembershipResp(await originalNext())
	}
	return transformed
}

export const fetchMyJoinedThreads = async (chatId, params = {}) => {
	try {
		const userId = getMyUserId()
		const resp = await get(`/users/${userId}/memberships`, {
			'filter[chat.is_thread]': 1,
			'filter[chat.parent_chat_id]': chatId,
			include: 'chat,chat.latest_user_message',
			'page[size]': 30,
			...params,
		})
		return transformMembershipResp(resp)
	} catch (e) {
		console.error('fetchMyJoinedThreads error:', e)
		throw e
	}
}

/**
 * Update a thread
 * @param {number} threadId - Thread chat ID
 * @param {Object} payload - Fields to update (e.g. { title })
 * @returns {Promise<Object>} Updated thread chat object
 */
export const updateThread = async (threadId, payload) => {
	try {
		const thread = await patch(`/chats/${threadId}`, payload)
		setChat(thread)
		return thread
	} catch (e) {
		console.error('updateThread error:', e)
		throw e
	}
}

/**
 * Delete a thread chat
 * @param {number} threadId - Thread chat ID
 * @returns {Promise<void>}
 */
export const deleteThread = async (threadId) => {
	try {
		await del(`/chats/${threadId}`)
		deleteChat(threadId)
	} catch (e) {
		console.error('deleteThread error:', e)
		throw e
	}
}

/**
 * Join a thread (add current user as member)
 * Uses existing membership endpoint
 * @param {number} threadId - Thread chat ID
 * @param {number} userId - User ID to add
 * @returns {Promise<Object>} Membership object
 */
export const joinThread = async (threadId, userId) => {
	try {
		const membership = await post(`/chats/${threadId}/memberships/${userId}`)
		setMembership(membership)
		return membership
	} catch (e) {
		console.error('joinThread error:', e)
		throw e
	}
}

/**
 * Leave a thread (remove current user from members)
 * Uses existing membership endpoint
 * @param {number} threadId - Thread chat ID
 * @param {number} userId - User ID to remove
 * @returns {Promise<void>}
 */
export const leaveThread = async (threadId, userId) => {
	try {
		const membership = getMyMembershipForBubble(threadId)
		await del(`/chats/${threadId}/memberships/${userId}`)
		if (membership) deleteMembership(membership.id)
	} catch (e) {
		console.error('leaveThread error:', e)
		throw e
	}
}
